"use client";
import {
  confirmResetPassword,
  fetchAuthSession,
  signIn,
  signInWithRedirect,
  signOut,
} from "aws-amplify/auth";
import { addHours } from "date-fns";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";

import useTracker from "@ctv/core/tracking/useTracker";
import {
  Button,
  Card,
  Text,
  Token,
  useLocalizedRouterServer,
} from "@ctv/shared-core/src";
import BlankHeader from "@ctv/shared/components/Layout/BlankHeader";
import { event, loginPageTracking } from "@ctv/shared/tracking/landing-page";
import { isEmail } from "@ctv/shared/utils/validator";

import { getQueryString } from "../query-string/query-string";
import { useContentResource } from "../resource";
import AuthLoading from "./AuthLoading";
import { useAuth } from "./CognitoAuthContext";
import Field from "./components/Field";
import ResendCodeModal from "./components/mfa-otp/ResendCodeModal";
import NewPasswordValidity, {
  validatePassword,
} from "./components/NewPasswordValidity";
import SuccessResetPassword from "./components/SuccessResetPassword";
import { decodeBase64, doForgetPassword } from "./utils/auth";

type EncryptedQs = {
  data?: string;
  nc?: boolean;
};
type DecryptedQs = {
  data?: {
    ea?: string;
    ed?: string; // Epoch date
  };
  nc?: boolean;
};

function CognitoLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pageState, setPageState] = useState<
    | "SIGN_IN"
    | "REQUEST_RESET_PASSWORD"
    | "RESET_PASSWORD_FORM"
    | "SUCCESS_RESET_PASSWORD"
  >("SIGN_IN");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPasswordValidity, setShowNewPasswordValidity] = useState(false);
  const [isResendCodeModalVisible, setIsResendCodeModalVisible] =
    useState(false);

  const track = useTracker();

  const { applyUser, isLoading, isAuthenticated } = useAuth();
  const router = useLocalizedRouterServer();
  const from = "/redirect/home";

  const { CorporateCognitoLogin } = useContentResource();

  function legacyLogic() {
    const query = getQueryString();

    if (isAuthenticated) {
      // Automatically detect if user is already logged in
      // Will logout the user and go to the reset password page
      if (query.ea) {
        const emailAddress = isEmail(query.ea)
          ? query.ea
          : decodeBase64(query.ea);

        if (emailAddress.length > 0 && isEmail(emailAddress)) {
          signOut().then(() => window.location.reload());
          return;
        }
      }
      router.replace(from);
    }

    if (query.ea) {
      // To handle if the email was sent from the BE (no clientmeta data)
      // TODO: Will remove this if the Forgot Password repository already support CryptoJS encryption
      const emailAddress = isEmail(query.ea)
        ? query.ea
        : decodeBase64(query.ea);

      if (emailAddress.length > 0 && isEmail(emailAddress)) {
        setEmail(emailAddress);
        setPageState("RESET_PASSWORD_FORM");
      }

      if (query.nc) {
        doForgetPassword(emailAddress).then((data) =>
          router.replace(`/login?data=${data}`)
        );
      }
    }
  }

  useEffect(() => {
    const qs = getQueryString();
    if (qs.ea) {
      legacyLogic();
      return;
    }

    const query = decryptQs(getQueryString<EncryptedQs>());

    if (isAuthenticated) {
      // Automatically detect if user is already logged in
      // Will logout the user and go to the reset password page
      if (query?.data) {
        const { ea: emailAddress } = query.data;
        if (emailAddress && isEmail(emailAddress)) {
          signOut().then(() => window.location.reload());
          return;
        }
      }
      router.replace(from);
    }

    handleQueryString(query);
    track(event.CORPORATE_TRAVEL, loginPageTracking.PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once
  }, [isAuthenticated]);

  function handleQueryString(query: ReturnType<typeof decryptQs>) {
    if (!query?.data?.ea) {
      return;
    }

    const { ea: emailAddress } = query.data;

    if (emailAddress.length === 0 || !isEmail(emailAddress)) {
      return;
    }

    handleResendCode(query);
    const lowerCaseEmail = emailAddress.toLowerCase();
    setEmail(lowerCaseEmail);
    setPageState("RESET_PASSWORD_FORM");

    if (!query?.nc) {
      return;
    }

    // Resend email and remove nc query string
    doForgetPassword(lowerCaseEmail).then((data) =>
      router.replace(`/login?data=${data}`)
    );
  }

  function handleResendCode(query: ReturnType<typeof decryptQs>) {
    if (!query?.data?.ed || !query?.data?.ea) {
      return;
    }

    const { ed: expiryDate } = query.data;
    const { ea: emailAddress } = query.data;
    const lowerCaseEmail = emailAddress.toLowerCase();

    const timeoutDate = addHours(Number(expiryDate), 1).getTime();
    setTimeout(() => {
      doForgetPassword(lowerCaseEmail).then((data) => {
        setIsResendCodeModalVisible(true);
        setCode("");
        router.replace(`/login?data=${data}`);
      });
    }, Math.max(timeoutDate - Date.now(), 0));
  }

  function handleLogin() {
    const lowerCaseEmail = email.toLowerCase();

    if (!isEmail(lowerCaseEmail)) {
      setErrorMessage(CorporateCognitoLogin.emailFieldError);
      return;
    }

    setIsSubmit(true);
    if (isTravelokaEmail(lowerCaseEmail)) {
      signInWithRedirect({
        provider: "Google",
      });
      return;
    }

    signIn({
      username: lowerCaseEmail,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
        preferredChallenge: "PASSWORD",
      },
    })
      .then((res) => {
        const { nextStep } = res;
        if (nextStep.signInStep === "DONE") {
          fetchAuthSession().then((res) => {
            applyUser(res.tokens?.idToken?.payload);
            router.replace(from);
          });
        }
        if (nextStep.signInStep === "RESET_PASSWORD") {
          setErrorMessage(CorporateCognitoLogin.forcedResetPasswordText);
          return;
        }
      })
      .catch((res: any) => {
        setErrorMessage(res.message);
        track(
          event.CORPORATE_TRAVEL,
          loginPageTracking.LOG_IN_BUTTON_ERROR(email, res.message)
        );
      })
      .finally(() => setIsSubmit(false));
    track(event.CORPORATE_TRAVEL, loginPageTracking.LOG_IN_BUTTON(email));
  }

  function handleForgotPassword() {
    if (!isEmail(email)) {
      setErrorMessage(CorporateCognitoLogin.emailFieldError);
      return;
    }

    setIsSubmit(true);
    doForgetPassword(email)
      .then(() => {
        setPageState("RESET_PASSWORD_FORM");
      })
      .catch((res) => setErrorMessage(res.message))
      .finally(() => setIsSubmit(false));
    track(
      event.CORPORATE_TRAVEL,
      loginPageTracking.FORGOT_PASSWORD_BUTTON(email)
    );
  }

  function handleConfirmNewPassword() {
    if (newPassword !== confirmNewPassword) {
      setErrorMessage(CorporateCognitoLogin.confirmPasswordNotMatchError);
      return;
    }

    if (!validatePassword(newPassword).isValid) {
      setShowNewPasswordValidity(true);
      return;
    }

    setIsSubmit(true);
    confirmResetPassword({
      username: email.toLowerCase(),
      confirmationCode: code.trim(),
      newPassword,
    })
      .then(() => setPageState("SUCCESS_RESET_PASSWORD"))
      .catch((res: any) => setErrorMessage(res.message))
      .finally(() => setIsSubmit(false));
  }

  function handleSubmit() {
    if (buttonDisabled) {
      return;
    }

    setErrorMessage("");
    if (pageState === "SIGN_IN") {
      handleLogin();
    } else if (pageState === "REQUEST_RESET_PASSWORD") {
      handleForgotPassword();
    } else if (pageState === "RESET_PASSWORD_FORM") {
      handleConfirmNewPassword();
    }
  }

  useEffect(() => {
    setErrorMessage("");

    if (pageState === "SUCCESS_RESET_PASSWORD") {
      router.shallow("/login/", "replace");
      setTimeout(() => setPageState("SIGN_IN"), 4000);
    }
  }, [pageState]);

  let showErrorMessage = Boolean(errorMessage);
  if (pageState === "SIGN_IN") {
    showErrorMessage = Boolean(errorMessage) && !isTravelokaEmail(email);
  }

  let buttonDisabled = !email || !password;
  if (isTravelokaEmail(email) || pageState === "REQUEST_RESET_PASSWORD") {
    buttonDisabled = !email;
  } else if (pageState === "RESET_PASSWORD_FORM") {
    buttonDisabled = !newPassword || !confirmNewPassword || !code;
  }

  if (isLoading && !isSubmit) {
    return <AuthLoading />;
  }

  return (
    <>
      <View style={styles.container}>
        <BlankHeader />
        <Card style={styles.wrapper} elevation="float">
          <View style={styles.content}>
            {pageState === "SIGN_IN" && (
              <>
                <Text style={styles.loginHeaderText} variant="title-1">
                  {CorporateCognitoLogin.loginFormHeader}
                </Text>
                <Field
                  label={CorporateCognitoLogin.emailFieldLabel}
                  placeholder={CorporateCognitoLogin.emailFieldPlaceholder}
                  value={email}
                  onChange={setEmail}
                  handleSubmit={handleSubmit}
                />
                {!isTravelokaEmail(email) && (
                  <>
                    <Field
                      label={CorporateCognitoLogin.passwordFieldLabel}
                      placeholder={
                        CorporateCognitoLogin.passwordFieldPlaceholder
                      }
                      password
                      value={password}
                      onChange={setPassword}
                      handleSubmit={handleSubmit}
                    />
                    <Text
                      ink="interactive"
                      variant="title-3"
                      testID="button__request-forgot-password"
                      style={styles.toRequestResetPasswordText}
                      onPress={() => setPageState("REQUEST_RESET_PASSWORD")}
                    >
                      {CorporateCognitoLogin.forgotPasswordText}
                    </Text>
                  </>
                )}
              </>
            )}
            {pageState === "REQUEST_RESET_PASSWORD" && (
              <>
                <Text style={styles.infoText}>
                  {CorporateCognitoLogin.requestResetPasswordInfoText}
                </Text>
                <Field
                  label={CorporateCognitoLogin.emailFieldLabel}
                  placeholder={CorporateCognitoLogin.emailFieldPlaceholder}
                  value={email}
                  onChange={setEmail}
                  handleSubmit={handleSubmit}
                  testID="request-reset-password-email"
                />
              </>
            )}
            {pageState === "RESET_PASSWORD_FORM" && (
              <>
                <Text style={styles.infoText}>
                  {CorporateCognitoLogin.submitResetPasswordInfoText}
                </Text>
                <Field
                  label={CorporateCognitoLogin.emailFieldLabel}
                  placeholder={CorporateCognitoLogin.emailFieldPlaceholder}
                  value={email}
                  onChange={setEmail}
                  disabled
                />
                <Field
                  label={CorporateCognitoLogin.yourNewPasswordLabel}
                  placeholder={CorporateCognitoLogin.yourNewPasswordPlaceholder}
                  password
                  value={newPassword}
                  onChange={setNewPassword}
                  handleSubmit={handleSubmit}
                  testID="new-password"
                />
                <NewPasswordValidity
                  password={newPassword}
                  show={showNewPasswordValidity}
                />
                <Field
                  label={CorporateCognitoLogin.confirmNewPasswordLabel}
                  placeholder={
                    CorporateCognitoLogin.confirmNewPasswordPlaceholder
                  }
                  password
                  value={confirmNewPassword}
                  onChange={setConfirmNewPassword}
                  handleSubmit={handleSubmit}
                  testID="confirm-new-password"
                />
                <Field
                  label={CorporateCognitoLogin.verificationCodeLabel}
                  placeholder={
                    CorporateCognitoLogin.verificationCodePlaceholder
                  }
                  value={code}
                  onChange={setCode}
                  handleSubmit={handleSubmit}
                  testID="verification-code"
                />
              </>
            )}
            {pageState === "SUCCESS_RESET_PASSWORD" && <SuccessResetPassword />}
          </View>
          {showErrorMessage && (
            <Text style={styles.error} variant="ui-small" ink="destructive">
              {errorMessage}
            </Text>
          )}
          {pageState !== "SUCCESS_RESET_PASSWORD" && (
            <Button
              style={[styles.button]}
              // innerStyle={styles.innerButton} TODO
              text={
                pageState === "SIGN_IN"
                  ? CorporateCognitoLogin.loginButtonText
                  : pageState === "REQUEST_RESET_PASSWORD"
                  ? CorporateCognitoLogin.sendEmailButtonText
                  : CorporateCognitoLogin.resetPasswordButtonText
              }
              loading={isSubmit}
              disabled={buttonDisabled}
              onPress={handleSubmit}
              testID={
                pageState === "SIGN_IN"
                  ? "submit__sign-in"
                  : pageState === "REQUEST_RESET_PASSWORD"
                  ? "submit__request-reset-password"
                  : "submit__reset-password"
              }
            />
          )}
        </Card>
      </View>
      <ResendCodeModal
        isVisible={isResendCodeModalVisible}
        setIsVisible={setIsResendCodeModalVisible}
      />
    </>
  );
}

const styles = StyleSheet.create({
  loginHeaderText: {
    marginBottom: Token.spacing.m,
    marginRight: "auto",
  },
  container: {
    //@ts-ignore
    width: "100vw",
    //@ts-ignore
    height: "100vh",
    backgroundColor: Token.color.lightNeutral,
  },
  wrapper: {
    width: 432,
    margin: "auto",
    overflow: "visible",
    paddingHorizontal: Token.spacing.l,
    paddingTop: Token.spacing.m,
    paddingBottom: Token.spacing.xl,
  },
  travelokaLogo: {
    marginBottom: 10,
    padding: 11,
    alignItems: "center",
  },
  content: {
    alignItems: "center",
  },
  toRequestResetPasswordText: {
    marginVertical: Token.spacing.xs,
    marginRight: "auto",
  },
  infoText: {
    marginBottom: Token.spacing.m,
  },
  error: {
    marginVertical: Token.spacing.xs,
  },
  button: {
    color: "#fff",
    marginTop: Token.spacing.l,
  },
  innerButton: {
    width: 384,
  },
});

function isTravelokaEmail(email: string) {
  return isEmail(email) && email.endsWith("@traveloka.com");
}

function decryptQs(query: EncryptedQs): DecryptedQs | undefined {
  const obj: DecryptedQs = {};
  if (query.data) {
    const data = decodeBase64(decodeURIComponent(query.data));

    if (data) {
      obj.data = JSON.parse(data) as DecryptedQs["data"];
    }
  }

  if (query.nc) {
    obj.nc = query.nc;
  }

  if (Object.keys(obj).length === 0) {
    return undefined;
  }

  return obj;
}

export default CognitoLogin;
